"use client";

import CloseIcon from "@/components/icons/CloseIcon";
import { APP_ROUTES } from "@/constants/routes";
import { usePrice } from "@/helper/usePrice";
import { useSubscription } from "@/helper/useSubscription";
import { montserrat } from "@/lib/font";
import { ConfigData } from "@/models/config";
import { ECurrency } from "@/models/general";
import { OPTIONS_SUBSCRIPTION, subscription } from "@/models/subscription";
import { User } from "@/models/user";
import SubscriptionServices from "@/services/main/subscriptionServices";
import DoneIcon from "@mui/icons-material/Done";
import { Box, Button, Dialog, Divider, IconButton } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { memo } from "react";
import IconShoppingCart from "../../../../../../../../components/icons/IconShoppingCart";
import { useAppSelector } from "@/lib/hooks";
interface PopupSubscribeProps {
  handleClose: () => void;
  open: boolean;
  isLogin?: boolean;
  isPayment?: boolean;
  dataDetail?: any;
  isViewBySubscription?: boolean;
  locale: string;
  user?: User | null;
}

const PopupSubscribe = (props: PopupSubscribeProps) => {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const config = queryClient.getQueryData<ConfigData>(["config"]);
  const { currencyLocal } = useAppSelector((state) => state.auth);
  const {
    open,
    handleClose,
    isLogin,
    dataDetail,
    isViewBySubscription,
    locale,
    user,
  } = props;
  const router = useRouter();
  const { hasPermission } = useSubscription();
  const handleRedirectPay = () => {
    if (isLogin)
      router.push(
        APP_ROUTES(locale).PAYMENT.REPORT.ROOT.to.replace(":id", dataDetail?.id)
      );
    else
      router.push(
        `${APP_ROUTES(locale).REGISTER.to}?type=report&redirect=${encodeURIComponent(APP_ROUTES(locale).PAYMENT.REPORT.ROOT.to.replace(":id", dataDetail?.id))}`
      );
  };

  const { data: pricing } = useQuery<subscription>({
    queryKey: ["subscription-pricing-min", locale],
    queryFn: async () => {
      const response = await SubscriptionServices.getOneSubscriptionMin({
        options: [
          OPTIONS_SUBSCRIPTION.PREMIUM,
          OPTIONS_SUBSCRIPTION.INTERACTIVE,
        ],
        language: locale,
      });

      return response;
    },
    refetchOnWindowFocus: false,
    enabled: !!dataDetail,
  });

  const { getCostCurrency } = usePrice();
  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Box className={`pt-4 pb-8 border-gray_20 ${montserrat}`}>
        <Box className="gap-8 grid px-6 ">
          {!hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM) &&
            !isViewBySubscription && (
              <>
                <Box className="flex flex-col">
                  <Box className="h-4 relative">
                    <IconButton
                      className="!absolute !right-0 !top-1/2 !-translate-y-1/2 !bg-eerie_black_5 size-8 !p-1"
                      onClick={handleClose}
                    >
                      <CloseIcon className="text-[rgba(28, 28, 28, 0.4)]" />
                    </IconButton>
                  </Box>
                  <Box className="flex items-center">
                    <span className="text-gray_90 font-bold text-xl/7">
                      {t("popup_subscribe_title_full_access")}
                    </span>
                  </Box>
                </Box>
                <Box className="gap-2 grid">
                  <Box className="gap-2 flex">
                    <DoneIcon className="text-cimigo_blue_light_1" />
                    <p
                      translation-key="popup_subscribe_description_1"
                      className="text-gray_90 text-sm/6 font-normal m-0"
                    >
                      {t("popup_subscribe_description_1")}
                      <span className="font-semibold">
                        {dataDetail?.title}.
                      </span>
                    </p>
                  </Box>
                  <Box className="gap-2 flex">
                    <DoneIcon className="text-cimigo_blue_light_1" />
                    <p className="text-gray_90 text-sm/6 font-normal m-0">
                      {t("popup_subscribe_description_3")}{" "}
                      <span
                        className="font-semibold"
                        translation-key="popup_subscribe_description_3_amount"
                      >
                        {t("popup_subscribe_description_3_amount", {
                          amount: getCostCurrency(
                            Number(pricing?.amountShow),
                            user?.currency ?? config?.currency ?? ECurrency.USD,
                            config?.usdToVND,
                            config?.idrToVND
                          ).show,
                        })}
                      </span>
                    </p>
                  </Box>
                </Box>
                <Box>
                  <Link
                    href={APP_ROUTES(locale).PRICING.to}
                    className="!py-1 !px-4 gap-2 !grid !bg-ciBlue_900 w-full rounded-sm text-center no-underline hover:!bg-cimigo_blue_light_1 popup-subscribe-button-link"
                  >
                    <span
                      translation-key="popup_subscribe_button_text"
                      className="py-1 font-medium text-base/6 text-white normal-case"
                    >
                      {t("popup_subscribe_button_text")}
                    </span>
                  </Link>
                </Box>
                <Box>
                  <Divider>
                    <span
                      translation-key="popup_subscribe_or_text"
                      className="font-normal text-sm/6 text-[#1C1C1C]/[65%] px-[19px]"
                    >
                      {t("popup_subscribe_or_text")}
                    </span>
                  </Divider>
                </Box>
              </>
            )}
          <Box
            className={`${!hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM) ? "gap-4" : "gap-0"}  grid`}
          >
            {hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM) && (
              <Box className="h-4 relative">
                <IconButton
                  className="!absolute !right-0 !top-1/2 !-translate-y-1/2 !bg-eerie_black_5 size-8 !p-1"
                  onClick={handleClose}
                >
                  <CloseIcon className="text-[rgba(28, 28, 28, 0.4)]" />
                </IconButton>
              </Box>
            )}
            <Box className="flex justify-between items-center">
              <span
                className="text-gray_90 font-bold text-xl/7"
                translation-key="popup_subscribe_title"
              >
                {t("popup_subscribe_title")}
              </span>
            </Box>
            <Box>
              <p className="text-gray_90 text-sm/6 font-normal m-0">
                {t.rich("popup_subscribe_description", {
                  title: dataDetail?.title || "",
                  price: getCostCurrency(
                    dataDetail?.amount || 0,
                    user?.currency ?? config?.currency ?? ECurrency.USD,
                    config?.usdToVND,
                    config?.idrToVND
                  ).show,
                  strong: (chunks) => (
                    <span className="font-semibold">{chunks}</span>
                  ),
                })}
              </p>
            </Box>
          </Box>
          <Box>
            <Button
              variant="outlined"
              className="!bg-ciBlue_200 w-full !rounded !py-1 !pl-2 !pr-3 gap-2 !flex !border-0 hover:!bg-ciBlue_2 hover:!border-[0.1px] hover:!border-ciBlue_light_2"
              onClick={handleRedirectPay}
            >
              <Box className="h-6 w-6">
                <IconShoppingCart className="text-ciBlue_900" />
              </Box>
              <Box className="py-1 gap-[10px] grid">
                <span
                  className="font-medium text-base/6 text-ciBlue_900 normal-case"
                  translation-key="common_purchase_report_now" // Key for button text
                >
                  {t("common_purchase_report_now")}
                </span>
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default memo(PopupSubscribe);
